import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, formatBytes, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Film, Plus, Search, LogOut, Loader2, Calendar, MapPin, Image as ImageIcon, HardDrive, Link2, MoreVertical, Trash2, Activity } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

export const STATUSES = ["Draft", "Media Uploaded", "Ready to Render", "Rendering", "Ready for Review", "Shared", "Failed", "Archived"];

export function statusClass(s) {
  const m = {
    Draft: "bg-slate-500/20 text-slate-300 border-slate-500/30",
    "Media Uploaded": "bg-blue-500/20 text-blue-300 border-blue-500/30",
    "Ready to Render": "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
    Rendering: "bg-amber-500/20 text-amber-300 border-amber-500/30 animate-pulse",
    "Ready for Review": "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    Shared: "bg-gold/20 text-gold border-gold/40",
    Failed: "bg-red-500/20 text-red-300 border-red-500/30",
    Archived: "bg-zinc-600/20 text-zinc-400 border-zinc-600/30",
  };
  return m[s] || m.Draft;
}

const EVENT_TYPES = ["Wedding", "Birthday", "Corporate Event", "Engagement", "Anniversary", "Charity Event", "Christmas Party", "Other"];
const BOOTH_TYPES = [
  ["", "Not set"],
  ["magic_mirror", "Magic Mirror"],
  ["vintage_rustic", "Vintage Rustic"],
  ["halo_selfie", "Halo Selfie"],
  ["video_360", "360 Video"],
  ["classic_enclosed", "Classic Enclosed"],
  ["open_air", "Open Air"],
  ["gif_booth", "GIF Booth"],
];

export default function Dashboard() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", client_name: "", event_type: "Wedding", booth_type: "", event_date: "", venue: "", description: "", guest_expiry: "", guest_password: "", internal_notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events", { params: { search, status: status === "all" ? "" : status } });
      setEvents(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Please enter an event name");
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.guest_password) delete payload.guest_password;
      const { data } = await api.post("/events", payload);
      toast.success("Event created");
      setOpen(false);
      setForm({ name: "", client_name: "", event_type: "Wedding", booth_type: "", event_date: "", venue: "", description: "", guest_expiry: "", guest_password: "", internal_notes: "" });
      nav(`/events/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreating(false);
    }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and ALL its media, outputs and guest links? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div className="min-h-screen bg-navy-deep grain">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-navy-deep/80 border-b border-navy-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo chip height={38} />
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-muted-foreground">{user?.email}</span>
            <Button data-testid="diagnostics-nav-button" variant="ghost" size="sm" onClick={() => nav("/diagnostics")} className="text-muted-foreground hover:text-ivory">
              <Activity className="w-4 h-4 mr-1.5" /> Diagnostics
            </Button>
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={logout} className="text-muted-foreground hover:text-ivory">
              <LogOut className="w-4 h-4 mr-1.5" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-ivory">Your Events</h1>
            <p className="text-muted-foreground mt-1">Create highlight reels and share them privately with your clients.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-event-button" className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold px-6 h-12 gold-glow">
                <Plus className="w-5 h-5 mr-1.5" /> Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-navy-card border-navy-border text-ivory max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Create Event</DialogTitle>
                <DialogDescription className="text-muted-foreground">Set up a new event project. You can add photos, videos and branding next.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2 space-y-1.5">
                  <Label>Event name *</Label>
                  <Input data-testid="event-name-input" value={form.name} onChange={set("name")} placeholder="Anna & Ben's Wedding" className="bg-navy-deep border-navy-border" />
                </div>
                <div className="space-y-1.5">
                  <Label>Client or couple names</Label>
                  <Input data-testid="event-client-input" value={form.client_name} onChange={set("client_name")} placeholder="Anna & Ben" className="bg-navy-deep border-navy-border" />
                </div>
                <div className="space-y-1.5">
                  <Label>Event type</Label>
                  <Select value={form.event_type} onValueChange={set("event_type")}>
                    <SelectTrigger data-testid="event-type-select" className="bg-navy-deep border-navy-border"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Booth type</Label>
                  <Select value={form.booth_type || "none"} onValueChange={(v) => set("booth_type")(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="event-booth-select" className="bg-navy-deep border-navy-border"><SelectValue placeholder="Not set" /></SelectTrigger>
                    <SelectContent>{BOOTH_TYPES.map(([v, l]) => <SelectItem key={v || "none"} value={v || "none"}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Event date</Label>
                  <Input data-testid="event-date-input" type="date" value={form.event_date} onChange={set("event_date")} className="bg-navy-deep border-navy-border" />
                </div>
                <div className="space-y-1.5">
                  <Label>Venue</Label>
                  <Input data-testid="event-venue-input" value={form.venue} onChange={set("venue")} placeholder="Glasgow" className="bg-navy-deep border-navy-border" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Description</Label>
                  <Textarea data-testid="event-description-input" value={form.description} onChange={set("description")} className="bg-navy-deep border-navy-border" rows={2} />
                </div>
                <div className="space-y-1.5">
                  <Label>Guest-page expiry</Label>
                  <Input data-testid="event-expiry-input" type="date" value={form.guest_expiry} onChange={set("guest_expiry")} className="bg-navy-deep border-navy-border" />
                </div>
                <div className="space-y-1.5">
                  <Label>Guest password (optional)</Label>
                  <Input data-testid="event-guest-password-input" value={form.guest_password} onChange={set("guest_password")} placeholder="Leave blank for none" className="bg-navy-deep border-navy-border" />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Internal notes</Label>
                  <Textarea data-testid="event-notes-input" value={form.internal_notes} onChange={set("internal_notes")} className="bg-navy-deep border-navy-border" rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="event-create-submit" onClick={create} disabled={creating} className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold w-full h-11">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Event"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="event-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by event, client or venue…" className="pl-10 h-11 bg-navy-card border-navy-border" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="status-filter-select" className="w-full sm:w-52 h-11 bg-navy-card border-navy-border"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-navy-border rounded-2xl">
            <Film className="w-12 h-12 text-navy-border mx-auto mb-4" />
            <p className="text-ivory text-lg font-medium">No events yet</p>
            <p className="text-muted-foreground text-sm mt-1">Create your first event to start building reels.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {events.map((ev) => (
              <div key={ev.id} data-testid={`event-card-${ev.id}`}
                className="group bg-navy-card border border-navy-border rounded-2xl p-6 hover:-translate-y-1 transition-transform cursor-pointer relative"
                onClick={() => nav(`/events/${ev.id}`)}>
                <div className="flex items-start justify-between mb-4">
                  <Badge variant="outline" className={`text-[11px] ${statusClass(ev.status)}`}>{ev.status}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`event-menu-${ev.id}`} className="text-muted-foreground hover:text-ivory p-1 rounded"><MoreVertical className="w-4 h-4" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-navy-card border-navy-border">
                      <DropdownMenuItem data-testid={`delete-event-${ev.id}`} className="text-red-400 focus:text-red-300" onClick={(e) => { e.stopPropagation(); del(ev.id, ev.name); }}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete event
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="font-display text-2xl text-ivory leading-tight group-hover:text-gold transition-colors">{ev.name}</h3>
                {ev.client_name && <p className="text-champagne text-sm mt-1">{ev.client_name}</p>}
                <div className="mt-5 space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gold" /> {formatDate(ev.event_date)} · {ev.event_type}</div>
                  {ev.venue && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gold" /> {ev.venue}</div>}
                  <div className="flex items-center gap-4 pt-1">
                    <span className="flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-gold" /> {ev.asset_count} assets</span>
                    <span className="flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5 text-gold" /> {formatBytes(ev.storage_used)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 pt-1">
                    <Link2 className="w-3.5 h-3.5 text-gold" /> Guest link: <span className="text-ivory">{ev.guest_link_status}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-navy-border text-[11px] text-muted-foreground">Created {formatDate(ev.created_at)}</div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
